import { useLayoutEffect } from 'react';

import { useRecoilState } from 'recoil';
import { bilingPlanCalcState } from 'recoils/biling/useBilingState';

import {
  calcInitCalcInfo,
  calcResetCalcInfo,
  calcTotalValue,
  costCalculateValue,
} from 'hooks/biling/useBilingCalc';
import { removeThreeComma } from 'utils/number/formatNumberUtil';

/**
 * @function useBilingTable
 * @desc 요금 계산기 테이블(BillingTable)의 슬라이더·입력·초기화 로직을 관리하는 커스텀 훅
 *
 * - Recoil(`bilingPlanCalcState`)로 전역 계산 상태(slider, cost, total)를 공유합니다.
 * - 페이지를 벗어날 때 상태를 자동 초기화합니다(useLayoutEffect cleanup).
 *
 * @returns {{
 *   values: { slider: number[], cost: (number|string)[], total: string },
 *   handleSliderChange: Function,
 *   handleInputChange: Function,
 *   handleResetClick: Function,
 * }}
 */
const useBilingTable = () => {
  const [values, setValues] = useRecoilState(bilingPlanCalcState);

  /**
   * 컴포넌트 언마운트 시 계산기 상태를 초기값으로 복원합니다.
   */
  useLayoutEffect(() => {
    return () => {
      setValues((prev) => {
        const initialValue = calcInitCalcInfo();
        return JSON.stringify(prev) === JSON.stringify(initialValue)
          ? prev
          : initialValue;
      });
    };
  }, []);

  /**
   * 슬라이더 값 변경 시 해당 행의 비용과 전체 합계를 갱신합니다.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @param {number} index
   * @param {number|string} chargeValue
   * @param {number} freeValue
   * @param {number} [fixValue]
   */
  const handleSliderChange = (event, index, chargeValue, freeValue, fixValue) => {
    const newValue = Number(event.target.value);

    setValues((prev) => {
      const newCost = prev.cost.map((value, i) =>
        i === index
          ? costCalculateValue(newValue, chargeValue, freeValue, fixValue)
          : value
      );
      return {
        ...prev,
        slider: prev.slider.map((value, i) => (i === index ? newValue : value)),
        cost: newCost,
        total: calcTotalValue(newCost),
      };
    });
  };

  /**
   * 텍스트 입력 변경 시 슬라이더 및 비용 상태를 갱신합니다.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @param {number} index
   * @param {number|string} chargeValue
   * @param {number} freeValue
   * @param {number} [fixValue]
   * @param {number} min
   * @param {number} max
   */
  const handleInputChange = (
    event,
    index,
    chargeValue,
    freeValue,
    fixValue,
    min,
    max
  ) => {
    const inputValue = removeThreeComma(event.target.value);
    const newValue =
      event.target.value === '' ? 0 : Math.max(min, Math.min(inputValue, max));

    setValues((prev) => {
      const newCost = prev.cost.map((value, i) =>
        i === index
          ? costCalculateValue(newValue, chargeValue, freeValue, fixValue)
          : value
      );
      return {
        ...prev,
        slider: prev.slider.map((value, i) => (i === index ? newValue : value)),
        cost: newCost,
        total: calcTotalValue(newCost),
      };
    });
  };

  /**
   * 초기화 버튼 클릭 시 모든 슬라이더와 비용을 0으로 리셋합니다.
   */
  const handleResetClick = () => {
    setValues(calcResetCalcInfo());
  };

  return {
    values,
    handleSliderChange,
    handleInputChange,
    handleResetClick,
  };
};

export default useBilingTable;
